import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

export const MODEL_TIERS = ["fast", "deep_thinking"] as const;
export type ModelTier = (typeof MODEL_TIERS)[number];

export const ITS_JUDGE_STRATEGIES = ["majority_vote", "ai_judge"] as const;
export type ItsJudgeStrategy = (typeof ITS_JUDGE_STRATEGIES)[number];

export const ITS_SAMPLE_COUNTS = [3, 5, 7] as const;

export const aiReasoningStep = defineStepConfig({
  type: "ai_reasoning",
  name: "AI Reasoning",
  description:
    "Leverage AI to reason over data. Takes a fully-formed prompt as input text. Optionally use a schema definition for structured output.",
  category: "AI & Agents",

  inputSchema: z.object({
    prompt: z
      .string()
      .min(1)
      .describe("The fully-formed prompt text to send to the model."),
  }),

  outputSchema: z.record(z.string(), z.unknown()),

  configSchema: z.object({
    systemPrompt: z
      .string()
      .max(10_000)
      .optional()
      .describe("Optional system prompt prepended to the conversation."),
    modelTier: z
      .enum(MODEL_TIERS)
      .default("fast")
      .describe("Which model tier to use for inference."),

    outputSchemaDefinitionId: z
      .string()
      .uuid()
      .optional()
      .describe(
        "Schema definition whose fields define structured output and output sockets. When unset, output is plain text.",
      ),
    outputSchemaDefinitionVersionId: z
      .string()
      .uuid()
      .optional()
      .describe("Pinned immutable version of the output schema."),

    itsEnabled: z
      .boolean()
      .default(false)
      .describe(
        "Enable inference time scaling (multiple parallel samples with judging).",
      ),
    itsSampleCount: z
      .number()
      .int()
      .min(3)
      .max(9)
      .optional()
      .describe(
        "Number of parallel AI calls to make. Odd numbers avoid ties in majority vote.",
      ),
    itsJudgeStrategy: z
      .enum(ITS_JUDGE_STRATEGIES)
      .optional()
      .describe("How to select the best response from N samples."),
    itsJudgePrompt: z
      .string()
      .max(10_000)
      .optional()
      .describe(
        "Custom prompt for the AI judge. Only used when strategy is ai_judge.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "7 minutes",
    retry: {
      maximumAttempts: 2,
    },
  },
});
